import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react'; // Icons for password visibility
import { signIn } from 'aws-amplify/auth'; // Import specific Auth function for sign-in

// Utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism input styles
const glassInput = 'w-full p-3 rounded-xl border border-transparent text-gray-900 placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A7F3D0] backdrop-blur-3xl';
// Button styles
const buttonBase = 'flex justify-center items-center gap-[10px] self-stretch rounded-lg h-14 px-[10px] font-semibold transition-colors duration-200 bg-[#1C731C] text-white hover:bg-[#155A15]';

const Login = ({ onLoginSuccess, onNavigateToForgotPassword }) => {
    // State variable for phone number input
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
    const [error, setError] = useState(''); // State for displaying login errors
    const [loading, setLoading] = useState(false); // State for loading indicator during login

    const handlePhoneNumberChange = (e) => {
        let value = e.target.value;
        // Ensure it starts with '+' and only allows digits after that
        if (!value.startsWith('+')) {
            // If user types numbers first, prepend +91
            if (/^\d+$/.test(value)) {
                value = '+91' + value;
            } else {
                // If it's not starting with + and not just digits, clear or guide
                value = ''; // Or provide specific error
            }
        }
        setPhoneNumber(value);
    };

    const handleLogin = async (e) => {
        e.preventDefault(); // Prevent default form submission behavior
        setError(''); // Clear any previous errors
        setLoading(true); // Show loading indicator

        // More robust E.164 validation
        // Pattern matches: + followed by 1 to 15 digits.
        const e164Pattern = /^\+[1-9]\d{1,14}$/; 
        if (!e164Pattern.test(phoneNumber)) {
            setError('Please enter a valid phone number in E.164 format (e.g., +919876543210).');
            setLoading(false);
            return;
        }

        try {
            // AWS Amplify Cognito Sign-In Call - Passing phone number as the 'username' explicitly
            const user = await signIn({ username: phoneNumber, password }); // Updated call to use an object
            console.log('Login successful:', user);
            onLoginSuccess(); // Trigger success callback to switch to HomePage or dashboard

            // --- Optional: Handle potential MFA challenges (if enabled in Cognito) ---
            // If your Cognito User Pool has MFA enabled, the signIn call might return a user object
            // with user.challengeName. You would then need to navigate to an MFA verification screen.
            // For simplicity, this example assumes direct login.
            // if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
            //     // Implement navigation to MFA verification component here
            // }

        } catch (err) {
            console.error('Error during login:', err);
            // Handle specific Cognito error codes for user feedback
            if (err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException') {
                setError('Invalid phone number or password.'); // Updated error message
            } else if (err.code === 'UserNotConfirmedException') {
                setError('Your account is not confirmed. Please verify your phone number.'); // Updated error message
            } else if (err.code === 'LimitExceededException') {
                 setError('Attempt limit exceeded, please try after some time.');
            }
            else {
                // Catch-all for other errors, including if Cognito explicitly says "username is required"
                setError(err.message || 'An unexpected error occurred during login. Please ensure the phone number is registered and verified in Cognito.');
            }
        } finally {
            setLoading(false); // Hide loading indicator
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }} // Initial animation state
            animate={{ opacity: 1, y: 0 }} // Animation target state
            transition={{ duration: 0.5 }} // Animation duration
            className="w-full" // Take full width of parent container
        >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center font-figtree leading-[1.2]">Login</h2>
            <form onSubmit={handleLogin} className="space-y-4"> {/* Form for login submission */}
                <div>
                    <label htmlFor="phoneNumber" className="block text-gray-800 text-lg font-medium mb-2 leading-[1.5]">
                        Phone Number
                    </label>
                    <input
                        type="tel" // Changed input type to 'tel' for phone numbers
                        id="phoneNumber" // Changed ID to phoneNumber
                        className={glassInput} // Apply glassmorphism input styles
                        placeholder="e.g., +919876543210" // Updated placeholder for E.164
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange} // Use custom handler for phone number input
                        required // HTML5 required validation
                        style={{ background: 'linear-gradient(151deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.00) 100%)' }}
                    />
                </div>
                <div>
                    <label htmlFor="password" className="block text-gray-800 text-lg font-medium mb-2 leading-[1.5]">
                        Password
                    </label>
                    <div className="relative"> {/* Container for password input and toggle button */}
                        <input
                            type={showPassword ? "text" : "password"} // Toggle input type for visibility
                            id="password"
                            className={combineClasses(glassInput, "pr-10")} // Apply styles, add padding for toggle button
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)} // Update password state on change
                            required // HTML5 required validation
                            style={{ background: 'linear-gradient(151deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.00) 100%)' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)} // Toggle showPassword state
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 hover:text-gray-900 focus:outline-none"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />} {/* Eye icon */}
                        </button>
                    </div>
                </div>
                {error && <p className="text-red-600 text-sm text-center">{error}</p>} {/* Display error message */}
                <button
                    type="submit"
                    className={combineClasses(
                        buttonBase, // Base button styles
                        "w-full", // Full width
                        "transition-all duration-300 transform hover:scale-105", // Hover effects
                        loading ? "opacity-70 cursor-not-allowed" : "" // Loading state styles
                    )}
                    disabled={loading} // Disable button when loading
                >
                    {loading ? 'Logging In...' : 'Log In'} {/* Button text changes based on loading state */}
                </button>
            </form>
            <div className="mt-6 text-center">
                <button
                    type="button"
                    onClick={() => onNavigateToForgotPassword(phoneNumber)} // Pass current phone number to forgot password flow
                    className="inline-block align-baseline text-lg font-poppins font-normal leading-[1.2] text-[#013220] hover:text-[#2E6B24] transition-colors"
                >
                    Forgot Password?
                </button>
            </div>
        </motion.div>
    );
};

export default Login;
